import { morph } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue } from '@theme/utilities';

/**
 * ColourLinksComponent
 *
 * Custom element that handles seamless navigation between colour variation products.
 * Each colour is a separate Shopify product with its own URL.
 *
 * Replicates the "loadsNewProduct -> morph main" path from Horizon's VariantPicker,
 * reading targets from data-connected-product-url on each radio input.
 */
class ColourLinksComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #isNavigating = false;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange.bind(this));
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (this.#isNavigating) return;

    const targetUrl = input.dataset.connectedProductUrl;
    const variantId = input.dataset.variantId;
    const currentUrl = this.dataset.productUrl?.split('?')[0];

    if (!targetUrl || targetUrl === currentUrl) return;

    if (window.Shopify?.designMode) {
      const fallbackUrl = this.#buildDisplayUrl(targetUrl, variantId);
      window.location.href = fallbackUrl;
      return;
    }

    this.#isNavigating = true;
    this.#setLoadingState(true);

    const requestUrl = this.#buildRequestUrl(targetUrl, variantId);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const newDoc = new DOMParser().parseFromString(html, 'text/html');

        const main = document.querySelector('main');
        const newMain = newDoc.querySelector('main');

        if (!main || !newMain) throw new Error('Could not find <main> element');

        const newHref = this.#buildDisplayUrl(targetUrl, variantId);
        yieldToMainThread().then(() => {
          history.replaceState({}, '', newHref);
        });

        morph(main, newMain);

        window.scrollTo({ top: 0, behavior: 'instant' });

        this.#isNavigating = false;
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;

        console.error('[colour-links] Navigation error, falling back to hard navigation:', err);

        const fallbackUrl = this.#buildDisplayUrl(targetUrl, variantId);
        window.location.href = fallbackUrl;
      })
      .finally(() => {
        this.#setLoadingState(false);
      });
  }

  #buildRequestUrl(productUrl, variantId) {
    const params = new URLSearchParams();

    const viewParam = getViewParameterValue();
    if (viewParam) params.set('view', viewParam);

    if (variantId) params.set('variant', variantId);

    const query = params.toString();
    return query ? `${productUrl}?${query}` : productUrl;
  }

  #buildDisplayUrl(productUrl, variantId) {
    const url = new URL(productUrl, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
    return url.toString();
  }

  #setLoadingState(loading) {
    this.setAttribute('aria-busy', String(loading));
    const fieldset = this.querySelector('.product-colour-links__fieldset');
    if (fieldset instanceof HTMLElement) {
      fieldset.style.opacity = loading ? '0.6' : '';
      fieldset.style.pointerEvents = loading ? 'none' : '';
    }
  }
}

if (!customElements.get('colour-links-component')) {
  customElements.define('colour-links-component', ColourLinksComponent);
}